import { useState } from "react";
import Button from "components/Button";
import NotesService from "api/NotesService";
import encryptNoteFields, {
  IEncryptedNoteFields,
} from "utils/encryptNoteFields";
import { CompanyKeyMissingError } from "utils/encryptForCompany";
import { ENotificationType, notification } from "utils/notification";
import { INote } from "components/NotesDesk";
import RichEditor from "components/RichEditor";
import Input from "components/Input";
import NewSearchCases from "components/NewSearchCases";
import { useTranslation } from "react-i18next";
import styles from "./formStyles.module.scss";

export interface AddNoteFormProps {
  toggle: () => void;
  refetch: () => void;
  addNote: (note: INote) => void;
  caseId?: string;
  participantId?: number;
  invoiceId?: string;
  withCasePicker?: boolean;
  defaultValues?: {
    id: string;
    title: string | null;
    content: string | null;
  } | null;
}

const AddNoteForm = ({
  defaultValues,
  toggle,
  refetch,
  addNote,
  caseId,
  participantId,
  invoiceId,
  withCasePicker,
}: AddNoteFormProps) => {
  const { t } = useTranslation();
  const [content, setContent] = useState(defaultValues?.content ?? "");
  const [title, setTitle] = useState(defaultValues?.title ?? "");
  const [caseIdLocal, setCaseIdLocal] = useState<string | undefined>(caseId);

  const { mutate: addNoteS } = NotesService.useCreateNote();
  const { mutate: editNote } = NotesService.useEditNote();

  const handleCreate = async () => {
    // Ta sama ścieżka co w AddNote/index.tsx i deskModule — jeden util zamiast trzech kopii
    // (utils/encryptNoteFields): treść szyfruje klucz OBIEKTU, owinięty kluczem FIRMY.
    let payload: IEncryptedNoteFields;
    try {
      payload = await encryptNoteFields(title, content);
    } catch (error) {
      // FAIL-CLOSED — patrz utils/encryptForCompany.ts.
      notification({
        type: ENotificationType.ERROR,
        title: t("errors.occurred"),
        text:
          error instanceof CompanyKeyMissingError
            ? error.message
            : t("errors.somethingWentWrong"),
      });
      return;
    }

    if (defaultValues?.id) {
      editNote(
        {
          Id: defaultValues.id,
          Title: payload.Title,
          Content: payload.Content,
          EncryptionKeyId: payload.EncryptionKeyId,
        },
        {
          onSuccess: () => {
            toggle();
            refetch();
          },
        },
      );
      return;
    }

    addNoteS(
      {
        Content: payload.Content,
        Title: payload.Title,
        EncryptionKeyId: payload.EncryptionKeyId,
        CaseId: caseIdLocal,
        ParticipantId: participantId,
        InvoiceId: invoiceId,
      },
      {
        onSuccess: (response) => {
          const note = response.data as INote;
          note.Title = title;
          note.Content = content;
          note.decoded = true;
          addNote(response.data);
          toggle();
        },
      },
    );
  };

  return (
    <div className={styles.form}>
      <div className={styles.inputs}>
        {withCasePicker && !defaultValues?.id && (
          <NewSearchCases onChoose={(ce) => setCaseIdLocal(ce.Id)} />
        )}
        <Input
          onChange={(a) => setTitle(a)}
          value={title}
          label={t("forms.subject")}
        />
      </div>
      <div className={styles.editor}>
        <RichEditor setContent={setContent} content={content} />
      </div>
      <div className={styles.button}>
        <Button onClick={handleCreate} text={t("common.save")} />
      </div>
    </div>
  );
};

export default AddNoteForm;
